// User-defined attributes: what ONE transaction screen knows about its
// additional attributes, shared by every component of that screen (header
// form, line grids, line forms, the list).
//
// TxnScreen provides it only when the host gave it `runtime.uda`; with no
// provider every hook in this module is inert (no requests, nothing rendered),
// so a host without additional attributes is byte-identical to before.
//
// The store remembers, per form, whether the form HAS a layer, learned from
// the cheapest evidence first: the `_uda` marker the engine puts on a load /
// list response of a form with attributes (plan §14), then the first
// `uda_resolve` answer. A form known to have none is never asked again on
// this screen — the rule that keeps a screen without attributes from calling
// uda_resolve over and over.

import { createContext, useContext, type ReactNode } from 'react'
import type { TxnScreenRuntime } from '../txn/screen/contract'
import type { UdaColumnDef, UdaResolution, UdaResolveRequest, UdaTransport } from './types'

export interface UdaScreenApi {
  objName: string
  /** The setup transport (Customize → Additional attributes). */
  transport: UdaTransport
  /** Run the `uda_resolve` read action (the screen's execute funnel). */
  resolve(req: UdaResolveRequest): Promise<UdaResolution>
  /** true / false once known, undefined while it is not. */
  layerKnown(formNo: string): boolean | undefined
  noteLayer(formNo: string, has: boolean): void
  /** The attribute columns last seen for a form (Customize's field panel). */
  columnsOf(formNo: string): UdaColumnDef[]
  noteColumns(formNo: string, columns: readonly UdaColumnDef[]): void
}

/** Normalise an answer so every field the UI reads exists. */
export function normalizeResolution(raw: unknown): UdaResolution {
  const r = (raw && typeof raw === 'object' ? raw : {}) as Partial<UdaResolution>
  return {
    ok: r.ok !== false,
    def_version: Number(r.def_version) || 0,
    deciding_fields: Array.isArray(r.deciding_fields) ? r.deciding_fields.map(String) : [],
    drivers: (r.drivers && typeof r.drivers === 'object') ? r.drivers : {},
    columns: Array.isArray(r.columns) ? r.columns.filter(c => c && typeof c === 'object') : [],
    sets: Array.isArray(r.sets) ? r.sets : [],
    rows: Array.isArray(r.rows) ? r.rows : [],
    flags: Array.isArray(r.flags) ? r.flags : [],
  }
}

/** Whether an answer says the form has a layer at all. A layer whose sets
 *  do not apply yet still names its deciding fields (or its flags). */
export function answerHasLayer(r: UdaResolution): boolean {
  return r.deciding_fields.length > 0 || r.columns.length > 0 || r.sets.length > 0
    || r.flags.length > 0 || r.rows.some(x => (x.attr_keys || []).length > 0)
}

export function createUdaScreenStore(opts: {
  objName: string
  transport: UdaTransport
  resolve: (req: UdaResolveRequest) => Promise<unknown>
}): UdaScreenApi {
  const layers = new Map<string, boolean>()
  const cols = new Map<string, UdaColumnDef[]>()
  return {
    objName: opts.objName,
    transport: opts.transport,
    resolve: async (req) => normalizeResolution(await opts.resolve(req)),
    layerKnown: (formNo) => layers.get(String(formNo)),
    noteLayer: (formNo, has) => { layers.set(String(formNo), has) },
    columnsOf: (formNo) => cols.get(String(formNo)) || [],
    noteColumns: (formNo, columns) => {
      const k = String(formNo)
      const prev = cols.get(k) || []
      const byName = new Map(prev.map(c => [String(c.name), c]))
      for (const c of columns) byName.set(String(c.name), c)
      cols.set(k, [...byName.values()])
    },
  }
}

const UdaScreenContext = createContext<UdaScreenApi | null>(null)

export function UdaScreenProvider({ value, children }: { value: UdaScreenApi | null; children: ReactNode }) {
  return <UdaScreenContext.Provider value={value}>{children}</UdaScreenContext.Provider>
}

/** The screen's additional-attributes store, or null (none on this host). */
export function useUdaScreen(): UdaScreenApi | null {
  return useContext(UdaScreenContext)
}

/** The screen runtime with the engine's `_uda` markers observed: a list or
 *  load response of a form says whether that form has attributes (present ⇒
 *  yes, absent ⇒ no), so the screen learns it from requests it makes anyway.
 *  `headerFormNo` names the form a request without `formNo` is for. */
export function observeUdaMarkers(
  rt: TxnScreenRuntime, store: UdaScreenApi | null, objName: string, headerFormNo: () => string,
): TxnScreenRuntime {
  if (!store) return rt
  const note = (formNo: string, res: unknown) => {
    try {
      const marker = (res && typeof res === 'object') ? (res as { _uda?: unknown })._uda : undefined
      store.noteLayer(formNo, !!marker && typeof marker === 'object')
    } catch { /* observing must never break a request */ }
  }
  return {
    ...rt,
    list: (obj, q) => rt.list(obj, q).then((res) => {
      if (obj === objName) note(q?.formNo ? String(q.formNo) : headerFormNo(), res)
      return res
    }),
    load: (obj, txnId) => rt.load(obj, txnId).then((res) => {
      if (obj === objName) note(headerFormNo(), res)
      return res
    }),
  }
}
